/* eslint-disable max-lines */
import React, { FunctionComponent } from 'react';
import { useTranslation } from '@wix/wix-i18n-config';
import { Box, FormField, MultiSelectCheckbox } from '@wix/design-system';
import css from './index.module.scss';
import { ArticleSortType } from '@wix/answers-api';
import { ALL_PRODUCTS_LABEL_ID } from '../../../../constants';
import { ALL_PRODUCTS_LABEL } from '../../../../pages/roadmap';
import { DATA_HOOKS } from '../../../../dataHooks';
import { SortAndFilterBiMetaData } from '../../../../types';

export type RoadmapSelectProductsProps = {
  selectedLabels: {
    id: string;
    name: string;
  }[];
  onSelectLabel: (label: { id: string; name: string }[]) => void;
  productLabels: { id: string; name: string }[];
  onChangeTriggerBiMetaData: (triggerBiMetaData: SortAndFilterBiMetaData) => void;
};
export const ALL_SORT_OPTION = [
  ArticleSortType.LAST_UPDATE_DATE_DESC,
  ArticleSortType.VOTES_DESC,
];

export const RoadmapSelectProducts: FunctionComponent<
  RoadmapSelectProductsProps
> = ({
  productLabels,
  selectedLabels,
  onSelectLabel,
  onChangeTriggerBiMetaData,
}) => {
  const { t } = useTranslation();

  const selectedOptionsLabels = React.useMemo(
    () =>
      (selectedLabels.length === 1 &&
        selectedLabels[0].id === ALL_PRODUCTS_LABEL_ID) ||
      !selectedLabels
        ? []
        : selectedLabels,
    [selectedLabels]
  );

  const allProductOptions = productLabels.map((productLabel) => ({
    id: productLabel.id,
    label: productLabel.name,
    value: productLabel.name,
  }));

  const onSelectProduct = React.useCallback(
    (id: string | number) => {
      const productName = productLabels.find((label) => label.id === id)?.name;
      onChangeTriggerBiMetaData({ filter_by: productName });
      if (id === ALL_PRODUCTS_LABEL_ID) {
        onSelectLabel(productLabels);
      } else {
        onSelectLabel(
          productLabels.filter(
            (option) =>
              selectedOptionsLabels.map((p) => p.id).includes(option.id) ||
              option.id === id
          )
        );
      }
    },
    [onSelectLabel, selectedOptionsLabels]
  );

  const onDeselectProduct = React.useCallback(
    (id: string | number) => {
      if (id === ALL_PRODUCTS_LABEL_ID) {
        onSelectLabel([ALL_PRODUCTS_LABEL]);
      } else {
        onSelectLabel(
          selectedOptionsLabels.filter(
            (option) => option.id !== id && option.id !== ALL_PRODUCTS_LABEL_ID
          )
        );
      }
    },
    [onSelectLabel, selectedOptionsLabels]
  );

  const isAllProductSelected = selectedOptionsLabels
    .map((selectedLabel) => selectedLabel.id)
    .includes(ALL_PRODUCTS_LABEL_ID);
  const valueForSelectProduct =
    selectedOptionsLabels.length > 0
      ? isAllProductSelected
        ? t('roadmap.select.product.value.all-product')
        : selectedOptionsLabels.length === 1
        ? t('roadmap.select.product.singular.value')
        : t('roadmap.select.product.value', {
            number: selectedOptionsLabels.length,
          })
      : undefined;

  const onClear = () => {
    onDeselectProduct(ALL_PRODUCTS_LABEL_ID);
  };

  return (
    <Box className={css.RoadmapSelectProducts}>
      <FormField
        dataHook={DATA_HOOKS.ROADMAP_SELECT_PRODUCTS_WRAPPER}
        label={t('roadmap.product-header')}
      >
        <MultiSelectCheckbox
          value={valueForSelectProduct}
          options={allProductOptions}
          selectedOptions={selectedOptionsLabels.map((l) => l.id)}
          border="round"
          dataHook={DATA_HOOKS.ROADMAP_SELECT_PRODUCTS}
          onClear={onClear}
          onSelect={onSelectProduct}
          onDeselect={onDeselectProduct}
          placeholder={t('roadmap.select-product.placeholder')}
        />
      </FormField>
    </Box>
  );
};
